import { useState, useRef, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { CheckCircle, AlertCircle, Plus, Trash2, Users, Search, Gift } from 'lucide-react'
import { findPartyByMemberName, updatePartyRSVP } from '../data/guests'
import { RSVPParty } from '../types'
import { useAutocomplete } from '../hooks/useAutocomplete'
import { submitRSVP } from '../services/googleSheets'

const rsvpSchema = z.object({
  searchName: z.string().min(1, 'Please enter your name'),
  partyMembers: z.array(z.object({
    id: z.string(),
    name: z.string(),
    isAttending: z.boolean(),
    isAttendingWelcomeDrinks: z.enum(['yes', 'no']).or(z.literal('')).transform(val => val === '' ? false : val === 'yes'),
    mealChoice: z.enum(['steak', 'mole', 'vegetarian']).optional().or(z.literal('')).transform(val => val === '' ? undefined : val),
    notes: z.string().optional()
  })),
  additionalPlusOnes: z.array(z.object({
    name: z.string().min(1, 'Please enter plus one name'),
    isAttendingWelcomeDrinks: z.enum(['yes', 'no']).or(z.literal('')).transform(val => val === '' ? false : val === 'yes'),
    mealChoice: z.enum(['steak', 'mole', 'vegetarian']).optional().or(z.literal('')).transform(val => val === '' ? undefined : val),
    notes: z.string().optional()
  })).optional(),
  specialRequests: z.string().optional()
}).refine((data) => {
  // If anyone is attending, they must have a meal choice
  const attendingMembers = data.partyMembers.filter(member => member.isAttending)
  const attendingPlusOnes = data.additionalPlusOnes || []
  
  // If no one is attending, validation passes
  if (attendingMembers.length === 0 && attendingPlusOnes.length === 0) {
    return true
  }
  
  // Check that all attending members have meal choices
  const membersWithoutMeals = attendingMembers.filter(member => !member.mealChoice)
  const plusOnesWithoutMeals = attendingPlusOnes.filter(plusOne => !plusOne.mealChoice)
  
  return membersWithoutMeals.length === 0 && plusOnesWithoutMeals.length === 0
}, {
  message: "All attending guests must select a meal choice",
  path: ["root"]
}).refine((data) => {
  // A plus one can only be added if at least one main party member is attending
  const attendingMembers = data.partyMembers.filter(member => member.isAttending)
  const attendingPlusOnes = data.additionalPlusOnes || []
  
  // If there are plus ones but no attending main party members, validation fails
  if (attendingPlusOnes.length > 0 && attendingMembers.length === 0) {
    return false
  }
  
  return true
}, {
  message: "A plus one can only be added if at least one person from your main party is attending",
  path: ["additionalPlusOnes"]
})

type FormData = z.infer<typeof rsvpSchema>

export function RSVP() {
  const [step, setStep] = useState<'search' | 'attendance' | 'details' | 'success'>('search')
  const [party, setParty] = useState<RSVPParty | null>(null)
  const [searchError, setSearchError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
  const [partyCanAttend, setPartyCanAttend] = useState<boolean | null>(true)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  
  const searchInputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  
  const { suggestions, isLoading, clearSuggestions, refreshData, hasSuggestions } = useAutocomplete(searchQuery)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm<FormData>({
    resolver: zodResolver(rsvpSchema),
    defaultValues: {
      partyMembers: [],
      additionalPlusOnes: []
    }
  })

  const { fields: plusOneFields, append: addPlusOne, remove: removePlusOne, replace: replacePlusOnes } = useFieldArray({
    control,
    name: 'additionalPlusOnes'
  })

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        !searchInputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || !hasSuggestions) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedSuggestionIndex >= 0) {
          selectSuggestion(suggestions[selectedSuggestionIndex])
        } else if (suggestions.length > 0) {
          selectSuggestion(suggestions[0])
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
        break
    }
  }

  const selectSuggestion = async (suggestion: any) => {
    setSearchQuery(suggestion.name)
    setShowSuggestions(false)
    setSelectedSuggestionIndex(-1)
    clearSuggestions()
    setIsSearchLoading(true)
    
    try {
      // Automatically find and proceed to attendance check
      const foundParty = await findPartyByMemberName(suggestion.name)
      if (foundParty) {
        proceedToAttendance(foundParty, suggestion.name)
      } else {
        setSearchError('Name not found on guest list')
      }
    } catch (error) {
      console.error('Error finding party:', error)
      setSearchError('An error occurred while searching. Please try again.')
    } finally {
      setIsSearchLoading(false)
    }
  }

  const resetSearchState = () => {
    setSearchQuery('')
    setSearchError('')
    setShowSuggestions(false)
    clearSuggestions()
  }

  const resetRSVPState = () => {
    setStep('search')
    setParty(null)
    setSearchQuery('')
    setSearchError('')
    setPartyCanAttend(true)
    setShowSuggestions(false)
    setSelectedSuggestionIndex(-1)
    clearSuggestions()
    // Reset form
    setValue('searchName', '')
    setValue('partyMembers', [])
    replacePlusOnes([])
    setValue('specialRequests', '')
  }

  const proceedToAttendance = (foundParty: RSVPParty, searchName: string) => {
    setValue('searchName', searchName)
    setParty(foundParty)
    setSearchError('')
    
    // Initialize attendance state based on existing RSVP data
    if (foundParty.hasRSVPed) {
      // Check if anyone in the party is attending
      const hasAttendingMembers = foundParty.members.some(member => member.isAttending)
      setPartyCanAttend(hasAttendingMembers)
    } else {
      // Default to true for new RSVPs
      setPartyCanAttend(true)
    }
    
    setStep('attendance')
  }

  const proceedToDetails = (foundParty: RSVPParty) => {
    // Separate original members from plus ones
    const originalMembers = foundParty.members.filter(member => member.type !== 'plus-one')
    const plusOnes = foundParty.members.filter(member => member.type === 'plus-one')

    // Initialize form with original party members
    const partyMembers = originalMembers.map(member => ({
      id: member.id,
      name: member.name,
      isAttending: member.isAttending ?? (foundParty.hasRSVPed ? false : true),
      isAttendingWelcomeDrinks: member.isAttendingWelcomeDrinks === true ? 'yes' : member.isAttendingWelcomeDrinks === false ? 'no' : '',
      mealChoice: member.mealChoice || undefined,
      notes: member.notes || ''
    }))

    setValue('partyMembers', partyMembers as any)

    // Initialize plus ones if they exist
    if (plusOnes.length > 0) {
      const additionalPlusOnes = plusOnes.map(plusOne => ({
        name: plusOne.name,
        isAttendingWelcomeDrinks: plusOne.isAttendingWelcomeDrinks === true ? 'yes' : plusOne.isAttendingWelcomeDrinks === false ? 'no' : '',
        mealChoice: plusOne.mealChoice || undefined,
        notes: plusOne.notes || ''
      }))
      replacePlusOnes(additionalPlusOnes as any)
    } else {
      // Clear plus ones if none exist
      replacePlusOnes([])
    }

    // Set special requests if they exist
    if (foundParty.specialRequests) {
      setValue('specialRequests', foundParty.specialRequests)
    }

    setStep('details')
  }

  const handleAttendanceResponse = (canAttend: boolean) => {
    setPartyCanAttend(canAttend)
  }

  const handleAttendanceContinue = () => {
    if (partyCanAttend === true) {
      // Proceed to detailed form
      if (party) {
        proceedToDetails(party)
      }
    } else if (partyCanAttend === false) {
      // Show confirmation modal before declining
      setShowConfirmModal(true)
    }
  }

  const handleDeclineSubmission = async () => {
    if (!party) return

    setIsSubmitting(true)
    
    try {
      // Create decline data for submission
      const declineData: FormData = {
        searchName: searchQuery,
        partyMembers: party.members.map(member => ({
          id: member.id,
          name: member.name,
          isAttending: false,
          isAttendingWelcomeDrinks: false,
          mealChoice: undefined,
          notes: ''
        })),
        additionalPlusOnes: [],
        specialRequests: ''
      }

      // Update party RSVP status
      await updatePartyRSVP(party.id, declineData)

      // Submit decline to Google Sheets
      const result = await submitRSVP(declineData, party)
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit RSVP')
      }
      
      // Refresh autocomplete data to reflect changes
      refreshData()
      setStep('success')
    } catch (error) {
      console.error('Error submitting decline:', error)
      // More detailed error logging
      if (error instanceof Error) {
        console.error('Error message:', error.message)
        console.error('Error stack:', error.stack)
      }
      alert('There was an error submitting your response. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSearchSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const name = searchQuery.trim()
    
    if (!name) {
      setSearchError('Please enter your name')
      return
    }

    setIsSearchLoading(true)
    setSearchError('')

    try {
      const foundParty = await findPartyByMemberName(name)
      if (!foundParty) {
        setSearchError('Name not found on guest list')
        return
      }

      proceedToAttendance(foundParty, name)
    } catch (error) {
      console.error('Error finding party:', error)
      setSearchError('An error occurred while searching. Please try again.')
    } finally {
      setIsSearchLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    setSearchError('')
    setShowSuggestions(value.length >= 2)
    setSelectedSuggestionIndex(-1)
  }

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setShowSuggestions(true)
    }
  }

  const onSubmit = async (data: FormData) => {
    if (!party) return

    // Check if everyone is declining
    const attendingMembers = data.partyMembers.filter(member => member.isAttending)
    const totalAttending = attendingMembers.length + (data.additionalPlusOnes?.length || 0)
    
    // If no one is attending, show confirmation modal
    if (totalAttending === 0) {
      setShowConfirmModal(true)
      return
    }

    // Continue with actual submission
    proceedWithSubmission(data)
  }

  const proceedWithSubmission = async (data: FormData) => {
    if (!party) return

    setIsSubmitting(true)
    
    try {
      await updatePartyRSVP(party.id, data)

      // Submit to Google Sheets
      const result = await submitRSVP(data, party)
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit RSVP')
      }

      // Refresh autocomplete data to reflect changes
      refreshData()
      setStep('success')
    } catch (error) {
      console.error('Error submitting RSVP:', error)
      alert('There was an error submitting your RSVP. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmDecline = async () => {
    setShowConfirmModal(false)
    await handleDeclineSubmission()
  }

  const handleCancelDecline = () => {
    setShowConfirmModal(false)
  }

  const partyMembers = watch('partyMembers') || []
  const attendingCount = partyMembers.filter(member => member.isAttending).length
  
  // Calculate remaining slots based on original members (not including existing plus ones)
  const remainingSlots = party ? (() => {
    if (!party.allowsAdditionalPlusOnes) return 0
    const originalMembersCount = party.members.filter(member => member.type !== 'plus-one').length
    const maxAdditionalSlots = party.maxPartySize - originalMembersCount
    return Math.max(0, maxAdditionalSlots - plusOneFields.length)
  })() : 0

  // Clear meal choice when member changes from attending to not attending
  useEffect(() => {
    partyMembers.forEach((member, index) => {
      if (!member.isAttending && member.mealChoice) {
        setValue(`partyMembers.${index}.mealChoice`, undefined)
      }
    })
  }, [partyMembers.map(m => m.isAttending).join(','), setValue])


  return (
    <div className="min-h-screen bg-neutral-50 relative">
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center shadow-xl">
            <div className="mb-4">
              <div className="animate-spin h-12 w-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-primary-900 mb-2">
              {party?.hasRSVPed ? 'Updating Your RSVP' : 'Submitting Your RSVP'}
            </h3>
            <p className="text-sm text-neutral-600">
              Please wait while we process your response...
            </p>
          </div>
        </div>
      )}

      <div className="section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto overflow-visible">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-serif text-primary-900 mb-4">RSVP</h1>
              <p className="text-lg text-neutral-600">
                Please let us know who from your party will be joining us
              </p>
            </div>

            {step === 'search' && (
              <div className="card p-8 overflow-visible">
                <h2 className="text-2xl font-serif text-primary-900 mb-6 text-center">
                  Find Your Invitation
                </h2>
                <p className="text-neutral-600 text-center mb-6">
                  Enter any name from your party
                </p>
                
                <div className="mb-6 p-4 sm:p-6 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start sm:items-center justify-center gap-3 text-blue-800">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <p className="font-medium text-center text-sm sm:text-base">
                      Please RSVP by <strong>February 28, 2026</strong>
                    </p>
                  </div>
                </div>
                
                <div className="mb-6 p-4 sm:p-6 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3 text-amber-800">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <p className="font-medium text-sm sm:text-base leading-relaxed">
                      We love your little ones, but to allow all guests to relax and enjoy the evening, our wedding will be an <strong>adults-only</strong> celebration. We truly appreciate your understanding.
                    </p>
                  </div>
                </div>
                <form onSubmit={handleSearchSubmit} className="space-y-6 overflow-visible">
                  <div className="relative">
                    <label htmlFor="searchName" className="block text-sm font-medium text-neutral-700 mb-2">
                      Your Name
                    </label>
                    <div className="relative">
                    <input
                        ref={searchInputRef}
                        id="searchName"
                        name="searchName"
                      type="text"
                        value={searchQuery}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onKeyDown={handleKeyDown}
                        className="input-field pr-10"
                        placeholder="Start typing your name..."
                      required
                        autoComplete="off"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        {isLoading ? (
                          <div className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" />
                        ) : (
                          <Search className="h-4 w-4 text-neutral-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Autocomplete Suggestions */}
                    {showSuggestions && hasSuggestions && (
                      <div
                        ref={suggestionsRef}
                        className="absolute z-[9999] w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                        style={{
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                      >
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={suggestion.id}
                            type="button"
                            onClick={() => selectSuggestion(suggestion)}
                            className={`w-full text-left px-4 py-3 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none border-b border-neutral-100 last:border-b-0 transition-colors ${
                              index === selectedSuggestionIndex ? 'bg-primary-50' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-primary-900">
                                  {suggestion.name}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                  </div>
                  
                  {(searchError || (searchQuery.length >= 2 && !hasSuggestions && !isLoading && !showSuggestions && step === 'search')) && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 text-red-800">
                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">
                            {searchError ? 'Name not found' : 'No matches found'}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            Try a different spelling or contact us for help:{' '}
                            <a href="mailto:eduardo.rios.jr.7@gmail.com" className="underline hover:text-red-600">
                              eduardo.rios.jr.7@gmail.com
                            </a>
                            {' or '}
                            <a href="mailto:naetzy@gmail.com" className="underline hover:text-red-600">
                              naetzy@gmail.com
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    disabled={isSearchLoading}
                    className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSearchLoading && (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    )}
                    {isSearchLoading ? 'Searching...' : 'Find My Party'}
                  </button>
                </form>
              </div>
            )}

            {step === 'attendance' && party && (
              <div className="card p-8 overflow-visible">
                <div className="text-center mb-8">
                  <Users className="h-12 w-12 text-primary-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-serif text-primary-900 mb-2">
                    Your Party
                  </h2>
                  
                  {party.hasRSVPed && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-center gap-2 text-blue-700 mb-2">
                        <CheckCircle className="h-5 w-5" />
                        <p className="font-semibold">RSVP Already Submitted</p>
                      </div>
                      <p className="text-sm text-blue-700">
                        Submitted on {party.rsvpSubmittedAt ? new Date(party.rsvpSubmittedAt).toLocaleDateString() : 'Unknown date'}
                        {party.rsvpSubmittedBy && ` by ${party.rsvpSubmittedBy}`}
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        You can update your response below.
                      </p>
                    </div>
                  )}
                  
                  <p className="text-neutral-600">
                    Will anyone from your party be able to attend our wedding?
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Party Members List */}
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-neutral-700 mb-3">Your Party Includes:</h3>
                    <div className="space-y-2">
                      {party.members.map((member) => (
                        <div key={member.id} className="flex items-center text-sm text-neutral-600">
                          <div className={`w-2 h-2 rounded-full mr-3 ${
                            member.type === 'plus-one' ? 'bg-amber-400' : 'bg-primary-400'
                          }`}></div>
                          <span className="font-medium">{member.name}</span>
                          {member.type === 'plus-one' && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full ml-2">
                              Plus One
                            </span>
                          )}
                        </div>
                      ))}
                      {/* Show plus one eligibility if applicable */}
                      {party.allowsAdditionalPlusOnes && 
                       !party.members.some(member => member.type === 'plus-one') && 
                       remainingSlots > 0 && (
                        <div className="flex items-center text-sm text-neutral-600">
                          <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                          <span className="font-medium italic">Plus one available</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attendance Question */}
                  <div className="space-y-4">
                    <div
                      onClick={() => handleAttendanceResponse(true)}
                      className={`w-full p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        partyCanAttend === true 
                          ? 'border-primary-400 bg-primary-50' 
                          : 'border-primary-200 hover:border-primary-400 hover:bg-primary-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-4">
                            ✓
                          </div>
                    <div>
                            <div className="font-medium text-primary-900 mb-1">
                              Yes, we can attend! 🎉
                            </div>
                            <div className="text-sm text-neutral-600">
                              Continue to provide details for each person
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center">
                          <input
                            type="radio"
                            name="attendance"
                            checked={partyCanAttend === true}
                            onChange={() => handleAttendanceResponse(true)}
                            className="w-5 h-5 text-primary-600 border-neutral-300 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      onClick={() => handleAttendanceResponse(false)}
                      className={`w-full p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        partyCanAttend === false 
                          ? 'border-neutral-400 bg-neutral-50' 
                          : 'border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-6 h-6 bg-neutral-100 text-neutral-600 rounded-full flex items-center justify-center mr-4">
                            ✕
                          </div>
                          <div>
                            <div className="font-medium text-neutral-900 mb-1">
                              Sorry, we can't make it 😔
                            </div>
                            <div className="text-sm text-neutral-600">
                              We'll miss celebrating with you
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center">
                          <input
                            type="radio"
                            name="attendance"
                            checked={partyCanAttend === false}
                            onChange={() => handleAttendanceResponse(false)}
                            className="w-5 h-5 text-primary-600 border-neutral-300 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        resetSearchState()
                        setStep('search')
                      }}
                      className="btn-secondary w-full sm:flex-1"
                      disabled={isSubmitting}
                    >
                      Back to Search
                    </button>
                    <button
                      type="button"
                      onClick={handleAttendanceContinue}
                      className="btn-primary w-full sm:flex-1"
                      disabled={isSubmitting || partyCanAttend === null}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 'details' && party && (
              <div className="card p-8">
                <div className="text-center mb-8">
                  <Users className="h-12 w-12 text-primary-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-serif text-primary-900 mb-2">
                    Your Party
                  </h2>
                  <p className="text-neutral-600">
                    Please let us know who will be attending from your party
                  </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                  {/* Party Members */}
                  <div>
                    <h3 className="text-lg font-medium text-primary-900 mb-4">Your Party</h3>
                    <div className="space-y-4">
                      {partyMembers.map((member, index) => (
                        <div key={member.id} className="border border-neutral-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-primary-900">{member.name}</h4>
                            <div className="flex items-center space-x-4">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  {...register(`partyMembers.${index}.isAttending`)}
                                  className="mr-2 text-primary-600"
                                />
                                <span className="text-sm font-medium text-primary-900">Attending</span>
                              </label>
                            </div>
                          </div>
                          
                          {member.isAttending && (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                                    Meal Choice *
                                  </label>
                                  <select
                                    {...register(`partyMembers.${index}.mealChoice`)}
                                    className={`input-field text-sm ${errors.partyMembers?.[index]?.mealChoice ? 'border-red-500' : ''}`}
                                    required={member.isAttending}
                                  >
                                    <option value="">Select your meal</option>
                                    <option value="steak">Steak with Three-Chili Butter</option>
                                    <option value="mole">Chicken with Mole Negro</option>
                                    <option value="vegetarian">Vegetarian Dish</option>
                                  </select>
                                  {errors.partyMembers?.[index]?.mealChoice && (
                                    <p className="mt-1 text-sm text-red-600">{errors.partyMembers[index]?.mealChoice?.message}</p>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                                    Attending Welcome Drinks on April 3rd? *
                                  </label>
                                  <select
                                    {...register(`partyMembers.${index}.isAttendingWelcomeDrinks`)}
                                    className={`input-field text-sm ${errors.partyMembers?.[index]?.isAttendingWelcomeDrinks ? 'border-red-500' : ''}`}
                                    required={member.isAttending}
                                  >
                                    <option value="">Select an option</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                  </select>
                                  {errors.partyMembers?.[index]?.isAttendingWelcomeDrinks && (
                                    <p className="mt-1 text-sm text-red-600">{errors.partyMembers[index]?.isAttendingWelcomeDrinks?.message}</p>
                                  )}
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">
                                  Special Notes
                                </label>
                                <input
                                  {...register(`partyMembers.${index}.notes`)}
                                  className="input-field text-sm"
                                  placeholder="Any special requests or dietary restrictions?"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Plus Ones */}
                  {party.allowsAdditionalPlusOnes && (
                    <div>
                      <div className="mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <h3 className="text-lg font-medium text-primary-900">Additional Plus One</h3>
                          {remainingSlots > 0 && attendingCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => addPlusOne({ name: '', isAttendingWelcomeDrinks: '' as any, mealChoice: undefined, notes: '' })}
                              className="btn-secondary text-sm flex items-center gap-2 justify-center sm:justify-start"
                            >
                              <Plus className="h-4 w-4" />
                              Add Plus One
                            </button>
                          ) : remainingSlots > 0 && attendingCount === 0 ? (
                            <div className="text-sm text-neutral-500 italic text-center sm:text-left">
                              A plus one can only be added if someone from your main party is attending
                            </div>
                          ) : null}
                        </div>
                      </div>
                      
                      {/* Warning when plus ones exist but no main party members are attending */}
                      {plusOneFields.length > 0 && attendingCount === 0 && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-2 text-amber-800">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            <p className="text-sm">
                              <strong>Note:</strong> A plus one requires at least one person from your main party to be attending. 
                              Please select someone from your main party or remove the plus one below.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {plusOneFields.map((field, index) => (
                        <div key={field.id} className="border border-neutral-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-primary-900">Plus One</h4>
                            <button
                              type="button"
                              onClick={() => removePlusOne(index)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                    </div>
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">
                                  Name *
                                </label>
                                <input
                                  {...register(`additionalPlusOnes.${index}.name`)}
                                  className={`input-field text-sm ${errors.additionalPlusOnes?.[index]?.name ? 'border-red-500' : ''}`}
                                  placeholder="Plus one's full name"
                                  required
                                />
                                {errors.additionalPlusOnes?.[index]?.name && (
                                  <p className="mt-1 text-sm text-red-600">{errors.additionalPlusOnes[index]?.name?.message}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">
                                  Meal Choice *
                                </label>
                                <select
                                  {...register(`additionalPlusOnes.${index}.mealChoice`)}
                                  className={`input-field text-sm ${errors.additionalPlusOnes?.[index]?.mealChoice ? 'border-red-500' : ''}`}
                                  required
                                >
                                  <option value="">Select meal</option>
                                  <option value="steak">Steak with Three-Chili Butter</option>
                                  <option value="mole">Chicken with Mole Negro</option>
                                  <option value="vegetarian">Vegetarian Dish</option>
                                </select>
                                {errors.additionalPlusOnes?.[index]?.mealChoice && (
                                  <p className="mt-1 text-sm text-red-600">{errors.additionalPlusOnes[index]?.mealChoice?.message}</p>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-neutral-700 mb-1">
                                Attending Welcome Drinks on April 3rd? *
                              </label>
                              <select
                                {...register(`additionalPlusOnes.${index}.isAttendingWelcomeDrinks`)}
                                className={`input-field text-sm ${errors.additionalPlusOnes?.[index]?.isAttendingWelcomeDrinks ? 'border-red-500' : ''}`}
                                required
                              >
                                <option value="">Select an option</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                              {errors.additionalPlusOnes?.[index]?.isAttendingWelcomeDrinks && (
                                <p className="mt-1 text-sm text-red-600">{errors.additionalPlusOnes[index]?.isAttendingWelcomeDrinks?.message}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-neutral-700 mb-1">
                                Special Notes
                              </label>
                              <input
                                {...register(`additionalPlusOnes.${index}.notes`)}
                                className="input-field text-sm"
                                placeholder="Any special requests or dietary restrictions?"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Special Requests */}
                      <div>
                    <label htmlFor="specialRequests" className="block text-sm font-medium text-neutral-700 mb-2">
                      Special Requests (Optional)
                        </label>
                        <textarea
                      id="specialRequests"
                      {...register('specialRequests')}
                          rows={3}
                          className="input-field"
                      placeholder="Any special requests or accommodations needed?"
                        />
                      </div>

                  {/* Summary */}
                  <div className={`border rounded-lg p-4 ${
                    attendingCount + plusOneFields.length === 0 
                      ? 'bg-neutral-50 border-neutral-200' 
                      : 'bg-primary-50 border-primary-200'
                  }`}>
                    <h4 className="font-medium text-primary-900 mb-2">RSVP Summary</h4>
                    {attendingCount + plusOneFields.length === 0 ? (
                      <div>
                        <p className="text-neutral-700 mb-2">
                          <strong>0</strong> people attending from your party
                        </p>
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                          <p className="text-amber-800 text-sm flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            You are declining the invitation for your entire party. We'll miss celebrating with you!
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-primary-800">
                        <strong>{attendingCount + plusOneFields.length}</strong> people attending from your party
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      type="button"
                      onClick={() => setStep('attendance')}
                      className="btn-secondary w-full sm:flex-1"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full sm:flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isSubmitting && (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      )}
                      {isSubmitting 
                        ? (party?.hasRSVPed ? 'Updating...' : 'Submitting...') 
                        : (party?.hasRSVPed ? 'Update RSVP' : 'Submit RSVP')
                      }
                    </button>
                  </div>
                </form>
              </div>
            )}

            {step === 'success' && party && (
              <div className="card p-8 text-center">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-serif text-primary-900 mb-4">
                  Thank You!
                </h2>
                <p className="text-lg text-neutral-600 mb-6">
                  Your RSVP has been {party.hasRSVPed ? 'updated' : 'submitted'} successfully.
                </p>
                
                {partyCanAttend !== false && (attendingCount + plusOneFields.length > 0) ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <p className="text-green-800">
                      We're so excited to celebrate with you on April 4, 2026!
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-4">
                      <p className="text-neutral-700">
                        We're sorry you can't make it, but we understand. We'll miss you on our special day!
                      </p>
                    </div>
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                      <p className="text-primary-900 mb-3">
                        Can't celebrate in person? You can still honor us with a gift from our registry.
                      </p>
                      <Link
                        to="/registry"
                        className="inline-flex items-center gap-2 btn-primary"
                      >
                        <Gift className="h-4 w-4" />
                        View Registry
                      </Link>
                    </div>
                  </>
                )}
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <button
                    onClick={resetRSVPState}
                    className="btn-secondary"
                  >
                    Update My RSVP
                  </button>
                  <a href="/" className="btn-primary">
                    Return to Home
                  </a>
                </div>
                
                <p className="text-sm text-neutral-500">
                  You can update your RSVP anytime before <strong>February 28, 2026</strong>. 
                  For questions, contact us at{' '}
                  <a href="mailto:eduardo.rios.jr.7@gmail.com" className="text-primary-600 hover:text-primary-700">
                    eduardo.rios.jr.7@gmail.com
                  </a>
                  {' '}or{' '}
                  <a href="mailto:naetzy@gmail.com" className="text-primary-600 hover:text-primary-700">
                    naetzy@gmail.com
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-serif text-primary-900 mb-2">
                Decline Invitation?
              </h3>
              <p className="text-neutral-600 leading-relaxed">
                You are declining the invitation for your entire party. We'll miss celebrating with you, but we understand.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancelDecline}
                className="btn-secondary w-full sm:flex-1"
                disabled={isSubmitting}
              >
                Go Back
              </button>
              <button
                onClick={handleConfirmDecline}
                className="btn-primary w-full sm:flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                {isSubmitting ? 'Submitting...' : 'Yes, Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 